// src/libs/coupon/issued-coupon.ts
//
// 개인 발급 쿠폰(mgmt `shop-coupon-issues`) 기반 체크아웃 쿠폰.
//
// 종전 ~~`ai-sale.ts`~~ 는 코드 1개(2607AI)·대상 slug·기간이 소스에 하드코딩된 전역 코드였고
// 발급 개념이 없어 코드를 아는 누구나 몇 번이든 쓸 수 있었다. 이제 쿠폰은 회원에게 발급되고
// 서버(mgmt)가 소유·미사용·만료를 검증한다 — 이 모듈은 **표시·계산만** 담당한다.
import { CouponPolicy } from "@/api/product-api";
import type { OrderItemV2 } from "@/models/orders/OrderDraft";

/** mgmt `GET /api/v1/coupons/me` 응답 1건. */
export type MyCoupon = {
  id: string;
  campaign: string;
  /** 할인율(정수 퍼센트, 예: 10 = 10%). mgmt 가 발급 시점 캠페인 정의를 스냅샷한 값. */
  percent: number;
  /**
   * 통화별 최대 할인액 상한(키 = USD/KRW/JPY). **비어 있으면 상한 없음.**
   * 상한이 금액이라 통화별로 달라야 하므로 단일 값이 아니다(가격이 4타입 모델).
   */
  maxDiscount: Record<string, number>;
  startAt: number;
  expireAt: number;
  used: boolean;
  /**
   * 이 쿠폰이 적용될 **상품 SKU**. `""`(또는 없음) = 전 상품 대상.
   *
   * 장바구니 캠페인은 담긴 상품 종류마다 쿠폰 1장이 발급된다(기획 확정 2026-08-14, mgmt#1239).
   * 그래서 한 회원이 여러 장을 갖고, **장마다 적용 대상 라인이 다르다.**
   *
   * ⚠️ 이 값이 없으면 FE 는 여러 장을 받아도 대상을 몰라 전 상품에 적용하는데, mgmt 의
   * `requireCouponPolicyRespected` 가 **쿠폰이 붙지 않은 라인은 카탈로그 할인까지만** 허용하므로
   * draft 생성이 거부된다(화면과 결제가 갈리는 것이 아니라 결제가 아예 막힌다).
   *
   * 구 mgmt 응답에는 이 필드가 없다 → 옵셔널로 두고 없으면 전 상품으로 읽는다.
   */
  targetSku?: string;
  /**
   * 대상 라인 **수량 합**의 상한. `0`(또는 없음) = 제한 없음. 보상판매 쿠폰은 `1`(Pro 1대 — #6101).
   *
   * 넘으면 그 쿠폰을 **통째로 적용하지 않는다**(일부 수량만 깎지 않는다 — `discount` 는 라인 안에서
   * 개당 균일해야 해서 1대분만 깎을 방법이 없다). mgmt 는 넘는 draft 를 400
   * `coupon-quantity-exceeded` 로 거부하므로, 여기서 안 거르면 자동 적용이 결제를 막는다.
   *
   * ⚠️ 상한(`maxDiscount`)으로 대신할 수 없다 — 상한은 쿠폰이 *추가로* 깎는 금액에 걸려 EXCLUSIVE
   * 라인에선 기존 할인 몫만큼 새어 나간다(#6101 「웹 사전 점검」).
   */
  maxQuantity?: number;
};

// 쿠폰 에러 코드 단일 출처 — 서버(apply-coupon 응답)·클라(coupon-field 메시지 맵)가 같은 상수를 써야
// 한쪽만 리네임/오타 나도 컴파일에서 잡혀 desync 를 막는다.
export const COUPON_ERROR_CODE = {
  NOT_ELIGIBLE: "NOT_ELIGIBLE",
  ALREADY_APPLIED: "ALREADY_APPLIED",
  NOT_FOUND: "NOT_FOUND",
  NOT_USABLE: "NOT_USABLE",
  UNAUTHORIZED: "UNAUTHORIZED",
  CREATE_FAILED: "CREATE_FAILED",
  /**
   * 쿠폰이 적용된 draft 인데 **쿠폰 없는 원본(base)을 찾을 수 없다** — 변경·해제 불가(#5129).
   * base 포인터 쿠키가 만료됐거나(2시간), 다른 브라우저/기기에서 결제 링크를 열었을 때 난다.
   * 적용 자체는 유효하므로 UI 는 실패로 다루지 말고 **변경 버튼만 감춘다**.
   */
  NO_BASE: "NO_BASE",
  /**
   * 요청한 draft 가 **이미 주문됐다**(전환 한도 도달) — 다른 탭·기기에서 결제가 끝났다. 라우트는 새 draft 를
   * 만들지도, base 로 되돌리지도 않는다(409). UI 는 실패가 아니라 「이미 주문됨」으로 다룬다 — 결제 직전 저장의
   * mgmt 409 와 같이 화면을 잠근다(docs/payments/Inicis.md §3-2, docs/checkout-coupon-policy.md §3-11).
   */
  ALREADY_ORDERED: "ALREADY_ORDERED",
} as const;
export type CouponErrorCode =
  (typeof COUPON_ERROR_CODE)[keyof typeof COUPON_ERROR_CODE];

/**
 * 보유했지만 **이번 주문에는** 못 쓰는 이유(#5129 — "쓸 수 없는 쿠폰은 그 이유를 알 수 있게").
 * 유효기간·사용여부로 걸러진 쿠폰은 애초에 목록에 오르지 않으므로 여기 대상이 아니다.
 */
export const COUPON_UNUSABLE_REASON = {
  /** 카트 전 라인이 쿠폰 미적용 대상(EXCLUDED) — 예: 액세서리만 담은 주문. */
  NO_ELIGIBLE_ITEMS: "NO_ELIGIBLE_ITEMS",
  /** 대상 상품은 있으나 기존 할인이 쿠폰보다 커서 추가 할인이 0(EXCLUSIVE). */
  BIGGER_DISCOUNT_APPLIED: "BIGGER_DISCOUNT_APPLIED",
  /**
   * 상한이 정의된 쿠폰인데 **이 통화의 상한이 없어** 적용을 보류했다(EUR/GBP 한정).
   * 캠페인 데이터 문제이지 고객 상태가 아니다 — `BIGGER_DISCOUNT_APPLIED` 로 뭉뚱그리면
   * 할인이 하나도 없는데 "더 큰 할인이 적용 중"이라고 표시된다(#5129 가 고치려던 오표시).
   */
  CAP_NOT_DEFINED_FOR_CURRENCY: "CAP_NOT_DEFINED_FOR_CURRENCY",
  /**
   * 대상 상품 수량이 쿠폰의 `maxQuantity` 를 넘는다(예: 보상판매 쿠폰인데 Pro 2대). 고객이 수량을
   * 줄이면 쓸 수 있다 — 다른 사유와 달리 **고객이 할 수 있는 일이 있다.**
   */
  QUANTITY_EXCEEDED: "QUANTITY_EXCEEDED",
} as const;
export type CouponUnusableReason =
  (typeof COUPON_UNUSABLE_REASON)[keyof typeof COUPON_UNUSABLE_REASON];

/**
 * draft 하나에 실을 수 있는 쿠폰 장수 상한. **mgmt 와 같은 값이어야 한다**
 * (`OrderDraftService.MAX_COUPONS_PER_DRAFT`) — 서버가 먼저 거부하면 사용자는 이유를 알 수 없다.
 *
 * 목록 길이가 서버의 쿠폰 조회 횟수를 정하므로(클라이언트 입력) 방어값이고, 실제 장바구니의
 * 상품 종류 수는 여기 한참 못 미친다.
 */
export const MAX_COUPONS_PER_DRAFT = 20;

// 소수 없는 통화는 정수 반올림, 그 외 2자리. (KRW/JPY 포함하므로 필요.)
const ZERO_DECIMAL = new Set(["KRW", "JPY"]);
export function roundMoney(amount: number, currency: string): number {
  if (ZERO_DECIMAL.has(currency)) return Math.round(amount);
  return Math.round(amount * 100) / 100;
}

/**
 * 통화 단위로 **내림**. 상한 예산을 나눠 담을 때는 반올림하면 상한을 넘길 수 있어 내림해야 한다.
 */
export function floorMoney(amount: number, currency: string): number {
  if (ZERO_DECIMAL.has(currency)) return Math.floor(amount);
  return Math.floor(amount * 100) / 100;
}

/**
 * 사용 가능 여부(표시용). **권위는 서버**다 — mgmt 가 draft 생성 시 소유·미사용·만료를 다시 본다.
 * 여기서 거르는 목적은 쓸 수 없는 쿠폰을 UI 에 노출하지 않는 것뿐이다.
 */
export function isUsable(
  coupon: MyCoupon,
  nowMs: number = Date.now(),
): boolean {
  if (coupon.used) return false;
  if (coupon.percent <= 0) return false;
  if (nowMs < coupon.startAt) return false;
  return nowMs < coupon.expireAt;
}

/**
 * 쿠폰 1장을 items 에 적용한 결과. `items` 는 새 배열이며 원본을 변형하지 않는다.
 */
export type ApplyResult = {
  items: OrderItemV2[];
  /** 이번 쿠폰으로 추가된 총 할인액. 0 이면 적용 대상이 없었다는 뜻. */
  discountAdded: number;
  /**
   * 계산 이전에 **적용 자체를 보류**한 사유. 카트 구성으로는 추론할 수 없는 사유만 담는다
   * (예: 통화 상한 미정의). `couponUnusableReason` 에 그대로 넘기면 우선 채택된다.
   */
  unusableReason?: CouponUnusableReason;
};

/**
 * 발급 쿠폰을 items 에 적용한다. **라인별로** 계산하므로 부류가 다른 상품이 한 카트에 섞여도 각각
 * 다르게 처리된다 — 카트 전체에 걸리는 것은 상한 예산뿐이다.
 *
 * ### 상품별 부류 (mgmt `ShopProduct.couponPolicy`)
 * `policyBySlug` 로 slug → [CouponPolicy] 를 넘긴다. **서버가 파생을 끝낸 값**이므로 여기서
 * `productType` 으로 다시 판정하지 않는다(규칙이 두 곳에 있으면 화면과 결제 금액이 달라진다).
 * 맵에 없는 slug 는 종전 동작인 [CouponPolicy.STACK].
 *
 * - **STACK** — 현재 판매가(세일가) 위에 얹는다 = 곱할인
 *   `(price − discount) × (1 − rate)` = `price × (1 − 할인율) × (1 − 쿠폰율)`
 * - **EXCLUSIVE** — 기존 할인과 쿠폰 중 큰 쪽만. 쿠폰액은 **정가 기준**(세일을 *대체*하는 것이라
 *   세일가 기준이면 "세일을 버렸는데 세일가의 N%" 모순). 쿠폰이 더 작으면 추가 할인 0.
 * - **EXCLUDED** — 쿠폰 미적용. 상한 예산도 소비하지 않는다.
 *
 * ### 상한은 쿠폰 할인액에만 걸린다 (총 할인액이 아니다)
 * 기획 확정: 상한은 "쿠폰으로 최대 얼마"이며 세일 할인과 합산하지 않는다. 그래서 세일이 큰 상품에서
 * 쿠폰 할인이 0 원이 되는 일이 없다.
 *
 * ### 예산 배분 — STACK 먼저, 라인 합계 기준
 * `price`/`discount` 는 **개당(per-unit)** 금액이다(정본 `docs/order-item-pricing.md`, 합계를 쓰는
 * 곳들은 전부 `× qty`). 그래서 예산도 **라인 합계(개당 × qty)** 로 소진한다 — 종전에는 개당 값을
 * 그대로 차감해 `qty` 2 이상에서 상한이 배수로 뚫렸다(#5073).
 *
 * 배분 순서는 **STACK → EXCLUSIVE** 로 고정한다. EXCLUSIVE 가 예산을 먼저 먹으면 STACK 라인에
 * 남지 않아 고객에게 불리하고(STACK 은 항상 이득, EXCLUSIVE 는 쿠폰이 세일보다 클 때만 이득),
 * 카트에 담은 순서에 결과가 의존하게 된다.
 *
 * 예산이 라인 전체를 못 덮으면 개당으로 나눠 **내림**한다. `discount` 는 라인 안에서 개당 균일해야
 * 하므로 "합계가 정확히 상한" 은 원리적으로 불가능하다 — **상한 이하**를 받아들인다.
 */
/**
 * 상한 통화 누락 로그를 **조합당 1회만** 남긴다.
 *
 * `applyIssuedCoupon` 은 `useCheckoutCoupon`/`useCartCoupon` 의 `useMemo` 안에서 **보유 쿠폰마다**
 * 호출된다. 그냥 찍으면 카트가 리렌더될 때마다 `쿠폰 수 × 로그` 가 쌓여 정작 봐야 할 신호가 묻힌다.
 * 알람 목적(캠페인 데이터가 잘못됐다)에는 1회면 충분하다.
 */
const loggedCapMisses = new Set<string>();

function logCapMissOnce(
  level: "warn" | "error",
  currency: string,
  capMap: Record<string, number>,
): void {
  const defined = Object.keys(capMap).sort().join(",");
  const key = `${level}|${currency}|${defined}`;
  if (loggedCapMisses.has(key)) return;
  loggedCapMisses.add(key);

  if (level === "warn") {
    console.warn(
      `[coupon] maxDiscount has no cap for currency=${currency} (defined: ${defined}) — coupon skipped, 캠페인에 ${currency} 상한 추가 필요`,
    );
  } else {
    console.error(
      `[coupon] maxDiscount has no cap for legacy currency=${currency} (defined: ${defined}) — 무제한 적용 중, 캠페인 데이터 확인 필요`,
    );
  }
}

/**
 * 이 통화에서의 상한 해석. **[applyIssuedCoupon] 과 [assignCouponsToLines] 가 같은 판정을 써야 한다** —
 * 배정 단계가 "쓸 수 있다" 고 본 쿠폰을 적용 단계가 상한 미정의로 건너뛰면, 그 라인은 다른 쿠폰을
 * 쓸 수 있었는데도 할인을 하나도 못 받는다.
 *
 * `blocked` = 상한이 정의된 쿠폰인데 이 통화 기준이 없어 **적용을 보류**하는 경우(EUR/GBP 한정).
 */
function resolveCap(
  coupon: Pick<MyCoupon, "maxDiscount">,
  currency: string,
): {
  capMap: Record<string, number>;
  hasAnyCap: boolean;
  cap: number | undefined;
  isLegacyCurrency: boolean;
  blocked: boolean;
} {
  const capMap = coupon.maxDiscount ?? {};
  const hasAnyCap = Object.keys(capMap).length > 0;
  const cap = capMap[currency];
  // ★스코프 = **EUR/GBP 한정**. 기존 3통화(USD/KRW/JPY)는 종전 동작(상한 없으면 무제한)을 유지한다.
  // 일부 통화만 정의된 캠페인이 있으면 KR/JP 사용자의 살아 있는 쿠폰이 오늘부터 조용히 사라지는데
  // (`useCartCoupon` 이 discount>0 으로 필터), 그건 EU/GB 도입과 무관한 회귀다.
  // 기존 통화 누락은 error 로그로 관측만 하고 동작은 바꾸지 않는다 — 캠페인 데이터를
  // 확인한 뒤(admin#1445) 확장할지 정한다.
  const isLegacyCurrency =
    currency === "USD" || currency === "KRW" || currency === "JPY";
  return {
    capMap,
    hasAnyCap,
    cap,
    isLegacyCurrency,
    blocked: hasAnyCap && typeof cap !== "number" && !isLegacyCurrency,
  };
}

/**
 * 대상 라인 수량 합이 쿠폰의 `maxQuantity` 를 넘는가. **[applyIssuedCoupon] 과 [assignCouponsToLines] 가
 * 같은 판정을 써야 한다** — `resolveCap` 의 `blocked` 와 같은 이유다(배정이 쓸 수 있다고 본 쿠폰을 적용이
 * 건너뛰면 그 라인은 다른 쿠폰을 쓸 수 있었는데도 할인 0).
 *
 * 대상 라인 = `targetSku` 가 있으면 그 SKU 라인, 없으면 쿠폰이 붙을 수 있는(EXCLUDED 아닌) 라인.
 * ⚠️ 후자는 mgmt 의 술어를 확인하지 못했다 — `maxQuantity` 를 쓰는 캠페인이 지금은 SKU 쿠폰(보상판매)뿐이다.
 */
function exceedsMaxQuantity(
  items: OrderItemV2[],
  coupon: Pick<MyCoupon, "maxQuantity" | "targetSku">,
  policyBySlug: PolicyBySlug,
): boolean {
  const max = coupon.maxQuantity ?? 0;
  if (!(max > 0)) return false;
  const targetSku = coupon.targetSku ?? "";
  const qty = items
    .filter((it) =>
      targetSku
        ? it.sku === targetSku
        : resolvePolicy(it, policyBySlug) !== CouponPolicy.EXCLUDED,
    )
    .reduce((sum, it) => sum + Math.max(1, it.qty), 0);
  return qty > max;
}

/**
 * 쓸 수 있는 쿠폰 중 **수량 상한 때문에** 빠지는 것(#6101 QA 2026-11-04). 장바구니가 「수량을 줄이면 쓸 수 있다」를
 * 알리는 데 쓴다 — 결제 화면은 `QUANTITY_EXCEEDED` 사유를 쿠폰 목록에 보이지만, **해외는 장바구니에서 곧바로 GE
 * 결제창으로 가서** 그 화면을 안 거친다.
 *
 * ★판정을 다시 조합하지 않고 **적용 함수의 사유를 그대로** 쓴다(#6166 R1). `applyIssuedCoupon` 은 통화 상한 미정의를 수량보다
 * **먼저** 본다 — 수량만 따로 보면 「수량 + 통화 상한이 있는데 이 통화만 상한이 없는」 쿠폰을 「줄이면 쓸 수 있다」고 안내하지만
 * 줄여도 적용되지 않는다. 사유가 한 곳에서 나오면 순서까지 같다.
 */
export function couponsBlockedByQuantity(
  items: OrderItemV2[],
  currency: string,
  coupons: MyCoupon[],
  policyBySlug: PolicyBySlug = {},
  nowMs: number = Date.now(),
): MyCoupon[] {
  return coupons.filter(
    (c) =>
      isUsable(c, nowMs) &&
      applyIssuedCoupon(items, currency, c, policyBySlug).unusableReason ===
        COUPON_UNUSABLE_REASON.QUANTITY_EXCEEDED,
  );
}

/**
 * 쿠폰이 이 라인에 추가로 만드는 **개당** 할인액. 부류별 규칙의 단일 출처 —
 * [applyIssuedCoupon] 의 실제 적용과 [assignCouponsToLines] 의 배정 비교가 같은 식을 쓴다.
 */
/**
 * 개당 쿠폰 할인액. **랜딩의 "예상 금액" 표시도 이 함수를 쓴다** — 화면이 자기 식으로 계산하면
 * 결제 금액과 어긋나고, 그건 고객이 결제 단계에서 발견한다.
 *
 * 인자를 `price`/`discount` 로만 좁혀 둔 이유: 카탈로그 제품(카트에 담기지 않은 것)에도 쓸 수
 * 있어야 한다. 이 함수가 원래부터 그 두 필드만 봤다.
 */
export function unitCouponDiscount(
  it: Pick<OrderItemV2, "price" | "discount">,
  policy: CouponPolicy,
  percent: number,
  currency: string,
): number {
  if (policy === CouponPolicy.EXCLUDED) return 0;
  const rate = percent / 100;

  if (policy === CouponPolicy.EXCLUSIVE) {
    // 세일을 **대체**하는 것이므로 쿠폰액은 정가 기준으로 계산한다. 세일가 기준으로 하면
    // "세일을 버렸는데 세일가의 N%" 라는 모순이 되어 고객이 무조건 손해를 본다.
    const couponOnly = roundMoney(rate * it.price, currency);
    // 쿠폰이 기존 할인보다 작으면 기존 할인을 유지한다 → 추가 할인 0(적용해도 변화 없음).
    return Math.max(0, couponOnly - it.discount);
  }

  // STACK — 현재 판매가(세일가) 위에 얹는다. 결과는 곱할인:
  // (price − discount) × (1 − rate) = price × (1 − 할인율) × (1 − 쿠폰율)
  const salePrice = it.price - it.discount;
  if (salePrice <= 0) return 0;
  return roundMoney(rate * salePrice, currency);
}

/**
 * 남은 예산 `remaining` 에서 이 라인에 실제로 줄 **개당** 할인액. 0 이면 이 라인은 건너뛴다.
 *
 * 예산이 라인 전체를 못 덮으면 개당으로 나눠 **내림**한다 — `discount` 는 라인 안에서 개당 균일해야
 * 해서 "합계가 정확히 상한" 은 원리적으로 불가능하고, 상한 이하를 받아들인다. 그 결과 **실제 소진은
 * `min(라인할인, 상한)` 보다 작을 수 있다**(qty 로 나눠 내리므로).
 *
 * ⚠️ **적용([applyIssuedCoupon])과 배정([assignCouponsToLines])이 이 함수를 공유해야 한다.**
 * 배정이 내림 전 추정치로 예산을 차감하면 남은 예산을 **과소평가**해, 예산이 멀쩡히 남았는데도
 * 뒤 라인을 후보에서 떨어뜨린다. 극단적으로 qty 가 커서 개당 내림이 0 이 되는 라인은 실제로는
 * 예산을 **한 푼도** 쓰지 않는데, 추정치로는 상한 전액을 쓴 것으로 기록되어 그 쿠폰이 뒤 라인에서
 * 통째로 배제된다(= 카트 할인이 0 이 된다).
 */
function giveUnitFor(
  it: OrderItemV2,
  policy: CouponPolicy,
  percent: number,
  currency: string,
  remaining: number,
): number {
  const wantUnit = unitCouponDiscount(it, policy, percent, currency);
  if (wantUnit <= 0) return 0;
  const qty = Math.max(1, it.qty);
  const wantLine = wantUnit * qty;
  return wantLine <= remaining
    ? wantUnit
    : floorMoney(remaining / qty, currency);
}

export function applyIssuedCoupon(
  items: OrderItemV2[],
  currency: string,
  coupon: Pick<MyCoupon, "percent" | "maxDiscount"> &
    Partial<Pick<MyCoupon, "targetSku" | "maxQuantity">>,
  policyBySlug: PolicyBySlug = {},
  /**
   * 이 쿠폰이 손댈 수 있는 라인 인덱스. 생략하면 전부.
   *
   * [applyIssuedCoupons] 가 **라인당 1장** 배정을 지킬 때 쓴다 — 전 상품 쿠폰이 SKU 쿠폰에게
   * 배정된 라인까지 먹으면 두 장이 같은 라인에 겹쳐 서버가 draft 를 거부한다.
   */
  eligibleIndexes?: ReadonlySet<number>,
): ApplyResult {
  // ⚠️ `maxDiscount` 는 **통화별** 상한이라, 이 통화 키가 없을 때의 의미가 두 가지로 갈린다:
  //   (a) 맵이 비어 있음 = 상한 없는 쿠폰 → 무제한이 맞다
  //   (b) 맵에 다른 통화는 있는데 이 통화만 없음 = **상한 미정의** → 무제한으로 읽으면 안 된다
  // 종전엔 둘을 구분하지 않아 (b) 가 `POSITIVE_INFINITY` 로 흘렀다. EU/GB 통화 추가(#5153) 로
  // (b) 가 실제 경로가 됐다 — $50 캡 쿠폰이 €957 주문에 무제한 적용된다(리뷰 §2).
  const { capMap, hasAnyCap, cap, isLegacyCurrency } = resolveCap(
    coupon,
    currency,
  );
  if (hasAnyCap && typeof cap !== "number" && !isLegacyCurrency) {
    // 상한이 정의된 쿠폰인데 이 통화 기준이 없다 → 적용하지 않는다(무제한 적용보다 안전).
    // 상한 통화를 늘리는 쪽(mgmt 캠페인 정의)이 근본 해결.
    //
    // 사유를 **반환값에 실어** 보낸다 — 호출부가 카트 구성만 보고 추론하면 "더 큰 할인이
    // 이미 적용됨" 으로 잘못 표시된다(할인이 0인데).
    logCapMissOnce("warn", currency, capMap);
    return {
      items,
      discountAdded: 0,
      unusableReason: COUPON_UNUSABLE_REASON.CAP_NOT_DEFINED_FOR_CURRENCY,
    };
  }

  // 수량 상한을 넘으면 적용하지 않는다 — 일부 수량만 깎을 수 없다(`maxQuantity` 주석). 사유를 실어
  // 보내는 이유는 위 통화 상한과 같다(카트 구성으로 추론하면 「더 큰 할인」으로 오표시된다).
  if (exceedsMaxQuantity(items, coupon, policyBySlug)) {
    return {
      items,
      discountAdded: 0,
      unusableReason: COUPON_UNUSABLE_REASON.QUANTITY_EXCEEDED,
    };
  }

  // 기존 3통화에 상한이 비어 있으면 **동작은 종전대로(무제한)** 두되 관측은 남긴다.
  // 이 로그가 뜬다면 그 통화에서 지금도 상한이 무제한으로 뚫려 있다는 뜻이라 캠페인 확인이 필요하다.
  if (hasAnyCap && typeof cap !== "number" && isLegacyCurrency) {
    logCapMissOnce("error", currency, capMap);
  }
  // 상한 예산은 **라인 합계(= 개당 × qty)** 기준으로 소진한다. `price`/`discount` 는 개당 금액인데
  // (정본 `docs/order-item-pricing.md`, 합계를 쓰는 5곳이 전부 `× qty`) 종전에는 개당 값을 그대로
  // 차감해 qty 2 이상에서 상한이 배수로 뚫렸다.
  let remaining =
    typeof cap === "number" && cap > 0 ? cap : Number.POSITIVE_INFINITY;
  let discountAdded = 0;

  // 예산 배분 순서: **STACK 먼저, EXCLUSIVE 나중.** 상한이 있을 때 EXCLUSIVE 가 예산을 먼저 먹으면
  // STACK 라인에 남지 않아 고객에게 불리하다(EXCLUSIVE 는 쿠폰이 세일보다 클 때만 이득인데,
  // STACK 은 항상 이득이다). 순서 고정으로 카트에 담은 순서에 결과가 의존하지 않게 한다.
  const order = items
    .map((it, index) => ({ index, policy: resolvePolicy(it, policyBySlug) }))
    .sort((a, b) => rank(a.policy) - rank(b.policy) || a.index - b.index);

  // 이 쿠폰의 대상 SKU. 비어 있으면 전 상품(웰컴 쿠폰).
  const targetSku = coupon.targetSku ?? "";

  const giveByIndex = new Map<number, number>();
  for (const { index, policy } of order) {
    if (remaining <= 0) break;
    // 배정 밖 라인은 손대지 않는다(예산도 소비하지 않는다).
    if (eligibleIndexes && !eligibleIndexes.has(index)) continue;
    const it = items[index];
    // 대상 SKU 가 지정된 쿠폰은 그 SKU 라인에만. mgmt 가 같은 술어로 금액을 검증한다.
    if (targetSku && it.sku !== targetSku) continue;
    const qty = Math.max(1, it.qty);
    const giveUnit = giveUnitFor(
      it,
      policy,
      coupon.percent,
      currency,
      remaining,
    );
    if (giveUnit <= 0) continue;

    giveByIndex.set(index, giveUnit);
    remaining -= giveUnit * qty;
    discountAdded += giveUnit * qty;
  }

  // EXCLUSIVE 도 `discount` 에 **더한다** — `unitCouponDiscount` 가 "쿠폰액 − 기존 할인" 을 돌려주므로
  // 결과가 `max(기존, 쿠폰)` 이 된다. 별도 교체 분기가 필요 없다.
  const out = items.map((it, index) => {
    const give = giveByIndex.get(index);
    if (!give) return it;
    return { ...it, discount: it.discount + give };
  });

  return { items: out, discountAdded: roundMoney(discountAdded, currency) };
}

/** 부류별 정책. 카탈로그에 없으면 종전 동작인 [CouponPolicy.STACK]. */
export type PolicyBySlug = Record<string, CouponPolicy | undefined>;

function resolvePolicy(it: OrderItemV2, map: PolicyBySlug): CouponPolicy {
  return map[it.slug] ?? CouponPolicy.STACK;
}

/** 예산 배분 우선순위 — 작을수록 먼저. */
function rank(policy: CouponPolicy): number {
  return policy === CouponPolicy.STACK ? 0 : 1;
}

/**
 * 실할인액이 0 인 쿠폰이 **왜** 이번 주문에 안 되는지 (#5129).
 *
 * [applyIssuedCoupon] 을 다시 돌리지 않도록 이미 계산한 `discountAdded` 를 받는다 — 목록 화면은
 * 쿠폰마다 할인액을 이미 구해뒀고, 여기서 또 계산하면 같은 일을 두 번 한다.
 *
 * 반환이 null 이면 "쓸 수 있다"는 뜻이다(`discountAdded > 0`).
 *
 * `skipped` 는 [applyIssuedCoupon] 이 **계산 이전에 보류**한 사유다. 카트 구성만 봐서는 알 수 없으니
 * 넘겨받아 우선 채택한다 — 안 그러면 통화 상한 미정의가 "더 큰 할인이 이미 적용됨" 으로 표시된다.
 */
export function couponUnusableReason(
  items: OrderItemV2[],
  discountAdded: number,
  policyBySlug: PolicyBySlug = {},
  skipped?: CouponUnusableReason,
): CouponUnusableReason | null {
  if (discountAdded > 0) return null;
  if (skipped) return skipped;
  // 대상 라인이 하나라도 있는데 할인이 0 이면 기존 할인이 더 컸다는 뜻(EXCLUSIVE).
  const hasEligibleLine = items.some(
    (it) => resolvePolicy(it, policyBySlug) !== CouponPolicy.EXCLUDED,
  );
  return hasEligibleLine
    ? COUPON_UNUSABLE_REASON.BIGGER_DISCOUNT_APPLIED
    : COUPON_UNUSABLE_REASON.NO_ELIGIBLE_ITEMS;
}

/**
 * 라인마다 **쿠폰 1장씩** 배정한다. 반환은 `쿠폰 id → 그 쿠폰이 맡은 라인 인덱스`.
 *
 * ### 왜 "카트당 1장" 이 아니라 "라인당 1장" 인가
 * 장바구니 캠페인은 상품 종류마다 쿠폰 1장을 발급한다(mgmt#1239). 그래서 여러 장이 **동시에**
 * 적용돼야 하지만, 한 라인에 두 장을 겹쳐 적용하면 안 된다 — mgmt 의
 * `requireCouponPolicyRespected` 가 라인별 허용 할인율을 `max(전 상품 쿠폰, 그 SKU 쿠폰)` 으로
 * 보기 때문에, 두 장을 합산한 할인은 **draft 생성 단계에서 거부**된다.
 *
 * ### 라인별 선택 규칙
 * 후보는 "전 상품 쿠폰" + "그 라인 SKU 쿠폰" 이다. 그중 **그 라인에서 실제로 더 많이 깎는 쪽**을
 * 쓴다 — 서버가 허용하는 라인 상한이 `max(...)` 이므로 어느 쪽을 골라도 허용 범위 안이고, 실할인액
 * 기준이어야 고객에게 유리하다. 동률이면 만료가 먼저인 쪽(오래 남은 쿠폰을 아낀다), 그다음 id 순으로
 * **결정적**으로 정한다 — 카트 재렌더마다 배정이 흔들리면 표시 금액이 깜빡인다.
 *
 * ⚠️ **`percent` 로 비교하면 안 된다.** 쿠폰마다 통화 상한이 다르면 율이 높은 쪽이 오히려 덜 깎고
 * (50% 상한 $1 vs 10% 무상한), 이 통화 상한이 정의되지 않은 쿠폰(EUR/GBP)은 적용이 보류되어 **0**
 * 이다 — 그걸 배정하면 그 라인은 다른 쿠폰을 쓸 수 있었는데도 할인을 하나도 못 받는다.
 * 그래서 상한 판정(`resolveCap`)과 라인 할인 계산(`unitCouponDiscount`)을 적용 단계와 **공유**한다.
 *
 * 상한(`maxDiscount`)은 **한 라인의 한도가 아니라 그 쿠폰이 배정된 라인들이 나눠 쓰는 카트 전체
 * 예산**이다. 그래서 배정도 라인을 훑으며 **예산을 차감**한다(순회 순서는 적용 단계와 동일).
 * 라인마다 상한을 새로 잡으면 상한이 앞 라인에서 소진된 쿠폰이 뒤 라인까지 차지해, 그 라인은
 * 쓸 수 있는 다른 쿠폰이 있었는데도 할인 0 이 된다.
 */
export function assignCouponsToLines(
  items: OrderItemV2[],
  currency: string,
  coupons: MyCoupon[],
  policyBySlug: PolicyBySlug = {},
  nowMs: number = Date.now(),
): Map<string, Set<number>> {
  const usable = coupons.filter((c) => isUsable(c, nowMs));
  const assignment = new Map<string, Set<number>>();

  // 라인 순회 순서를 **적용 단계와 똑같이** 맞춘다(STACK 먼저 → 인덱스 순, [applyIssuedCoupon] 의
  // `order` 와 동일). 순서가 다르면 어느 라인이 공유 예산을 먼저 쓰는지가 갈려 추정이 실제와 어긋난다.
  const order = items
    .map((it, index) => ({ index, policy: resolvePolicy(it, policyBySlug) }))
    .sort((a, b) => rank(a.policy) - rank(b.policy) || a.index - b.index);

  // 쿠폰별 **남은 예산**. 상한은 그 쿠폰이 배정된 라인들이 나눠 쓰는 카트 전체 예산이므로,
  // 라인마다 상한을 새로 잡으면(= `min(라인할인, cap)` 만 보면) 이런 일이 난다:
  //   전 상품 쿠폰 G(15%, 상한 $100)가 라인 A(2000) 와 라인 B(100) 둘 다에서 이겨 배정 →
  //   적용 단계에서 A 가 $100 을 다 쓰고 B 는 **0** → B 전용 SKU 쿠폰 S(10%, 무상한, $10)를
  //   후보에서 떨어뜨린 게 그대로 손실이 된다.
  // 그래서 배정도 예산을 차감해가며 판단한다.
  const remainingBudget = new Map<string, number>();
  for (const c of usable) {
    const { cap, blocked } = resolveCap(c, currency);
    remainingBudget.set(
      c.id,
      blocked || exceedsMaxQuantity(items, c, policyBySlug)
        ? 0 // 이 통화 상한 미정의·수량 상한 초과 = 적용 보류 → 애초에 배정 대상이 아니다.
        : typeof cap === "number" && cap > 0
          ? cap
          : Number.POSITIVE_INFINITY,
    );
  }

  for (const { index, policy } of order) {
    const it = items[index];
    const qty = Math.max(1, it.qty);

    let picked: MyCoupon | null = null;
    let pickedGain = 0;
    for (const coupon of usable) {
      const targetSku = coupon.targetSku ?? "";
      if (targetSku && targetSku !== it.sku) continue;
      // 예산이 남지 않은 쿠폰은 후보가 아니다 — 배정해도 적용 단계에서 0 을 준다.
      const budget = remainingBudget.get(coupon.id) ?? 0;
      if (budget <= 0) continue;
      // 이 쿠폰을 이 라인에 쓰면 실제로 깎이는 금액. **`percent` 로 비교하면 안 된다** — 상한이
      // 쿠폰마다 다르면 율이 높은 쪽이 오히려 덜 깎는다(50% 상한 $1 vs 10% 무상한).
      // 적용 단계와 **같은 함수**로 구한다(내림까지 동일) — 그래야 아래 예산 차감이 실제와 맞는다.
      const couponGain =
        giveUnitFor(it, policy, coupon.percent, currency, budget) * qty;
      // 이 라인에서 아무것도 못 깎는 쿠폰은 후보가 아니다(배정을 낭비하지 않는다).
      // 개당 내림이 0 이 되는 라인(qty 가 상한보다 큰 경우)도 여기서 걸러진다 — 배정해두면
      // 적용 단계가 그 라인을 건너뛰어 예산만 묶인다.
      if (couponGain <= 0) continue;
      if (picked === null) {
        picked = coupon;
        pickedGain = couponGain;
        continue;
      }
      // 결정적 우선순위: 실할인액 큰 것 → 만료 먼저 → id 사전순.
      const better =
        couponGain > pickedGain ||
        (couponGain === pickedGain &&
          (coupon.expireAt < picked.expireAt ||
            (coupon.expireAt === picked.expireAt && coupon.id < picked.id)));
      if (better) {
        picked = coupon;
        pickedGain = couponGain;
      }
    }
    if (!picked) continue;

    const lines = assignment.get(picked.id) ?? new Set<number>();
    lines.add(index);
    assignment.set(picked.id, lines);
    // `pickedGain` 은 적용 단계와 같은 함수(내림 포함)로 구한 값이라 **실제 소진액과 일치**한다.
    // 내림 전 추정치를 빼면 남은 예산을 과소평가해, 예산이 남았는데도 뒤 라인을 버린다.
    remainingBudget.set(
      picked.id,
      (remainingBudget.get(picked.id) ?? 0) - pickedGain,
    );
  }

  return assignment;
}

/** 여러 장을 한 번에 적용한 결과. */
export type ApplyMultiResult = {
  items: OrderItemV2[];
  /** 적용된 쿠폰들의 할인액 합계. */
  discountAdded: number;
  /** 실제로 할인을 만든 쿠폰들. draft 에 실어 보낼 `couponIssueIds` 의 근거다. */
  appliedCoupons: MyCoupon[];
  /**
   * 쿠폰 id → 그 쿠폰이 만든 할인액. `appliedCoupons` 에 있는 id 만 들어온다.
   *
   * 표시용이지만 **여기서 함께 내보내는 이유**: 쿠폰 한 장을 따로 [applyIssuedCoupon] 에 넣어
   * 다시 계산하면 배정이 달라진다(혼자 있으면 전 라인이 후보가 된다) → 화면 합계가 실제 할인과
   * 어긋난다. 배정은 한 번만 하고 그 결과를 쪼개 보여준다.
   */
  discountByCouponId: Record<string, number>;
};

/**
 * 보유 쿠폰 **여러 장**을 items 에 적용한다. 각 쿠폰은 [assignCouponsToLines] 가 배정한 라인만
 * 건드리므로 **한 라인에 두 장이 겹치지 않는다.**
 *
 * 쿠폰별로 [applyIssuedCoupon] 을 그대로 호출한다 — 그래서 **상한 예산이 쿠폰마다 독립**이고,
 * 상품별 정책(STACK/EXCLUSIVE/EXCLUDED)·라인 합계 소진·통화 상한 판정이 한 벌로 유지된다.
 * 배정이 서로 겹치지 않으므로 적용 순서는 결과에 영향이 없다.
 *
 * ⚠️ 할인이 0 인 쿠폰은 `appliedCoupons` 에서 빠진다. draft 에 안 쓰이는 id 를 실어 보내면 서버가
 * `requireUsable` 을 통과시키고 **주문 확정 시 소진**해버려, 고객은 할인도 못 받고 쿠폰을 잃는다.
 */
export function applyIssuedCoupons(
  items: OrderItemV2[],
  currency: string,
  coupons: MyCoupon[],
  policyBySlug: PolicyBySlug = {},
  nowMs: number = Date.now(),
): ApplyMultiResult {
  const assignment = assignCouponsToLines(
    items,
    currency,
    coupons,
    policyBySlug,
    nowMs,
  );
  if (assignment.size === 0) {
    return {
      items,
      discountAdded: 0,
      appliedCoupons: [],
      discountByCouponId: {},
    };
  }

  // 배정 순서를 쿠폰 id 로 고정한다 — Map 삽입 순서(= 라인 순서)에 기대면 카트 정렬이 바뀔 때
  // 결과 배열의 동일성이 흔들려 불필요한 리렌더가 난다.
  //
  // ⚠️ **같은 id 는 한 번만** 돈다. 목록에 중복이 섞이면 같은 쿠폰이 두 번 적용돼 할인이 곱으로
  // 쌓이고(10% 두 번 = 19%) 상한도 장당 새로 잡혀 상한의 2배가 빠진다. 호출부(라우트)가 이미
  // 중복을 제거하지만, 이 함수가 언더차지의 최종 계산 지점이라 여기서도 막는다.
  const seen = new Set<string>();
  const ordered = coupons
    .filter((c) => {
      if ((assignment.get(c.id)?.size ?? 0) === 0) return false;
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    })
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  let acc = items;
  let total = 0;
  const appliedCoupons: MyCoupon[] = [];
  const discountByCouponId: Record<string, number> = {};

  for (const coupon of ordered) {
    const result = applyIssuedCoupon(
      acc,
      currency,
      coupon,
      policyBySlug,
      assignment.get(coupon.id),
    );
    if (result.discountAdded <= 0) continue;
    acc = result.items;
    total += result.discountAdded;
    appliedCoupons.push(coupon);
    discountByCouponId[coupon.id] = result.discountAdded;
  }

  return {
    items: acc,
    discountAdded: roundMoney(total, currency),
    appliedCoupons,
    discountByCouponId,
  };
}

// 종전 `pickBestCoupon`(카트당 최선의 1장)은 **없앴다.** 쿠폰이 상품 SKU 에 매이면서(mgmt#1239)
// 여러 장이 서로 다른 라인에 동시 적용되므로 "1장 고르기"는 남은 라인의 할인을 버리는 것이 된다.
// 자동 적용 대상은 [applyIssuedCoupons] 의 `appliedCoupons` 가 정본이다.
